import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  FlightTakeoff as FlightIcon,
  Visibility as ViewIcon,
  Settings as ManageIcon,
   Pallet as PalletIcon ,
  Add as AddIcon,
} from '@mui/icons-material';

const VuelosList = () => {
  const { hasPermission } = useAuth();
  const [vuelos, setVuelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(30);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchVuelos();
  }, [limit]);

  const fetchVuelos = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${endpoints.vuelos.list}?limit=${limit}`);
      setVuelos(response.data.data.vuelos);
      setTotal(response.data.data.pagination.total);
    } catch (error) {
      console.error('Error al cargar vuelos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (e) => {
    const value = e.target.value;
    setLimit(value === 'todos' ? 99999 : parseInt(value));
  };

  return (
    <Box>
      {/* Header Card */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <FlightIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Resumen de Vuelos
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.85 }}>
                Origen: MIA-LATAM - Últimos vuelos creados
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Mostrar</InputLabel>
              <Select
                value={limit === 99999 ? 'todos' : limit}
                onChange={handleLimitChange}
                label="Mostrar"
              >
                <MenuItem value={30}>Últimos 30 vuelos</MenuItem>
                <MenuItem value={60}>Últimos 60 vuelos</MenuItem>
                <MenuItem value={90}>Últimos 90 vuelos</MenuItem>
                <MenuItem value="todos">Todos los vuelos</MenuItem>
              </Select>
            </FormControl>

            {hasPermission('vuelos.crear') && (
              <Button
                component={Link}
                to="/vuelos/nuevo"
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
              >
                Nuevo Vuelo
              </Button>
            )}

            <Box sx={{ flex: 1 }} />

            <Typography variant="body2" color="text.secondary">
              Mostrando <strong>{vuelos.length}</strong> de <strong>{total}</strong> vuelo
              {total !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">AWB - Master</TableCell>
                <TableCell align="center">Destino</TableCell>
                <TableCell align="center">Fecha</TableCell>
                <TableCell align="center">Equipo</TableCell>
                <TableCell align="center">Pallets</TableCell>
                <TableCell align="center">Kilos</TableCell>
                <TableCell align="center">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Skeleton loading
                [...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(7)].map((_, cellIndex) => (
                      <TableCell key={cellIndex} align="center">
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : vuelos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <PalletIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">
                      No hay vuelos para mostrar
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                vuelos.map((vuelo) => (
                  <TableRow key={vuelo.id} hover>
                    <TableCell align="center">
                      <Typography fontWeight="bold">{vuelo.master || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={vuelo.destino}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {new Date(vuelo.fecha).toLocaleDateString('es-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell align="center">{vuelo.equipo_nombre}</TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<PalletIcon sx={{ fontSize: 16 }} />}
                        label={vuelo.tpallets || 0}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {(vuelo.tkilos || 0).toLocaleString()} kg
                    </TableCell>
                    <TableCell align="center">
                      {vuelo.cerrado ? (
                        <Button
                          component={Link}
                          to={`/vuelos/${vuelo.awb}/detalle`}
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<ViewIcon />}
                        >
                          Ver Detalle
                        </Button>
                      ) : (
                        <Button
                          component={Link}
                          to={`/vuelos/${vuelo.awb}/gestionar`}
                          variant="contained"
                          size="small"
                          startIcon={<ManageIcon />}
                        >
                          Gestionar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default VuelosList;

